import { Constants } from './constants';

/**
 * UKC Conformation judge schedule and run order rules, shared by the API,
 * the judging schedule export and both judge schedule tabs.
 *
 * - Novice Puppy and Veteran are judged inside each breed, so those dogs count
 *   toward their breed / breed group tile instead of getting a tile of their own.
 * - Total Dog and Total Junior are fee-only summary classes and are never
 *   judged on their own, so they get no tile, entries or ring time.
 */
export const UKC_CONFORMATION_IN_BREED_LEVELS: string[] = [
  Constants.dog_class_element_level.novice_puppy,
  Constants.dog_class_element_level.veteran,
];

export const UKC_CONFORMATION_SUMMARY_LEVELS: string[] = [
  Constants.dog_class_element_level.total_dog,
  Constants.dog_class_element_level.total_junior,
];

export function isUKCConformationShow(sanctioningClub?: string, showType?: string): boolean {
  return (
    sanctioningClub === Constants.sanctioning_club.UKC &&
    showType === Constants.show_type.conformation
  );
}

/** True for classes that never get their own judge schedule / run order tile. */
export function isUKCConformationUnscheduledLevel(level?: string): boolean {
  return (
    !!level &&
    (UKC_CONFORMATION_IN_BREED_LEVELS.includes(level) ||
      UKC_CONFORMATION_SUMMARY_LEVELS.includes(level))
  );
}

/**
 * Best in Show style judging blocks. Each one is scheduled as a break tile with a
 * `bis_class` and no entries, added when its class is offered on the show.
 */
const BIS = Constants.ukc_conformation_bis_class;

interface IUKCShowElementSelection {
  show_element?: string;
  level?: string;
  judge_name?: string;
  judge_number?: string;
}

export interface IUKCConformationBISBlock {
  bis_class: string;
  judge_name: string;
  judge_number: string;
}

/** BIS blocks for the selected show elements, in judging order, with the judge of the class that triggers each one. */
export function getUKCConformationBISBlocks(showElements: IUKCShowElementSelection[] = []): IUKCConformationBISBlock[] {
  const rules: { bis_class: string; matches: (se: IUKCShowElementSelection) => boolean }[] = [
    { bis_class: BIS.veteran, matches: (se) => se.level === Constants.dog_class_element_level.veteran },
    { bis_class: BIS.novice_puppy, matches: (se) => se.level === Constants.dog_class_element_level.novice_puppy },
    { bis_class: BIS.altered, matches: (se) => se.show_element === Constants.dog_class_element.conformation_altered },
    { bis_class: BIS.bis, matches: (se) => se.show_element === Constants.dog_class_element.conformation },
    { bis_class: BIS.total_dog, matches: (se) => se.level === Constants.dog_class_element_level.total_dog },
    { bis_class: BIS.total_junior, matches: (se) => se.level === Constants.dog_class_element_level.total_junior },
  ];

  return rules.flatMap(({ bis_class, matches }) => {
    const selected = showElements.filter(matches);
    if (selected.length === 0) return [];
    const judged = selected.find((se) => !!se.judge_name) || selected[0];
    return [{ bis_class, judge_name: judged.judge_name || '', judge_number: judged.judge_number || '' }];
  });
}

export function isUKCConformationBISTile(dogClass?: { type?: string; bis_class?: string }): boolean {
  return !!dogClass?.bis_class && dogClass.type === Constants.running_order_dog_class_type.break;
}

interface IBISSyncJudge {
  judge_name?: string;
  judge_number?: string;
  dog_classes?: any[];
}

interface IBISSyncRing<J extends IBISSyncJudge> {
  ring: string;
  running_order?: J[];
}

/**
 * Adds a BIS block for every selected class that isn't scheduled in any ring and
 * removes blocks whose class is no longer offered or that the club hid
 * (`show.hidden_bis_classes`). A new block goes to the judge of the class that
 * triggers it when that judge has a column, otherwise to the first judge of the
 * first ring judging Conformation. Returns null when nothing changed.
 */
export function syncUKCConformationBISTiles<J extends IBISSyncJudge, R extends IBISSyncRing<J>>(
  showElements: IUKCShowElementSelection[] | undefined,
  rings: R[],
  hiddenBisClasses: string[] = []
): R[] | null {
  const hidden = new Set(hiddenBisClasses);
  const blocks = getUKCConformationBISBlocks(showElements).filter((block) => !hidden.has(block.bis_class));
  const wanted = new Set(blocks.map((block) => block.bis_class));
  let changed = false;

  type SyncedJudge = J & { dog_classes: any[] };
  const nextRings: (R & { running_order: SyncedJudge[] })[] = rings.map((ring) => ({
    ...ring,
    running_order: (ring.running_order || []).map((judge): SyncedJudge => {
      const dogClasses = (judge.dog_classes || []).filter((dc) => !isUKCConformationBISTile(dc) || wanted.has(dc.bis_class));
      if (dogClasses.length !== (judge.dog_classes || []).length) changed = true;
      return { ...judge, dog_classes: dogClasses };
    }),
  }));

  const judges = nextRings.flatMap((ring) => ring.running_order);
  const scheduled = new Set(
    judges.flatMap((judge) => judge.dog_classes).filter(isUKCConformationBISTile).map((dc) => dc.bis_class)
  );
  const conformationJudge = nextRings
    .find((ring) => ring.running_order.some((judge) =>
      (judge.dog_classes || []).some((dc) => dc.show_element === Constants.dog_class_element.conformation)))
    ?.running_order[0];
  const defaultJudge = conformationJudge || judges[0];

  blocks.forEach((block) => {
    if (scheduled.has(block.bis_class)) return;
    const assigned = block.judge_name
      ? judges.find((judge) => judge.judge_name === block.judge_name && (judge.judge_number || '') === block.judge_number)
      : undefined;
    const target = assigned || defaultJudge;
    if (!target) return;
    target.dog_classes.push({
      type: Constants.running_order_dog_class_type.break,
      bis_class: block.bis_class,
      name: block.bis_class,
      duration: Constants.ukc_conformation_bis_default_minutes,
      total_minutes: Constants.ukc_conformation_bis_default_minutes,
    });
    changed = true;
  });

  return changed ? (nextRings as R[]) : null;
}

/** True when a dog class is judged as part of its breed (regular Conformation, Novice Puppy, Veteran). */
export function countsTowardUKCBreedTile(dogClass?: { show_element?: string; level?: string }): boolean {
  if (!dogClass) return false;
  if (dogClass.show_element === Constants.dog_class_element.conformation) return true;
  return (
    dogClass.show_element === Constants.dog_class_element.conformation_non_licensed &&
    UKC_CONFORMATION_IN_BREED_LEVELS.includes(dogClass.level || '')
  );
}
